import { collection, collectionGroup, doc, getDoc, getDocs, limit, query, serverTimestamp, setDoc, updateDoc, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "./firebase";
import { Globals } from "../globals";
import { gemeindeSubPath } from "./gemeindePaths";
import ChoirSong, { ChoirSongDetails, choirSongConverter } from "../DTOs/ChoirSong";
import { normalizeSongTitle, songDocId, songKey } from "../utils/songTitle";

// Repertoire administration (GOD-18): the song catalogue of a Gemeinde.

function songsCollection(gemeindeId: string) {
    return collection(db, ...gemeindeSubPath(gemeindeId, Globals.CHOIRSONG_SUBCOLLECTION));
}

/** All songs of a Gemeinde, sorted by title. One read per song, no performances. */
export async function loadChoirSongs(gemeindeId: string): Promise<ChoirSong[]> {
    const snapshot = await getDocs(songsCollection(gemeindeId).withConverter(choirSongConverter));
    return snapshot.docs.map((songDoc) => songDoc.data()).sort((a, b) => a.name.toString().localeCompare(b.name.toString(), "de"));
}

export class SongExistsError extends Error {
    constructor(public existingName: string) {
        super("song-exists");
    }
}

/** Adds a song to the catalogue. Throws SongExistsError when the title is taken (in any spelling). */
export async function createChoirSong(gemeindeId: string, name: string, details: ChoirSongDetails): Promise<string> {
    const title = normalizeSongTitle(name);
    const key = songKey(title);
    if (!key) throw new Error("Invalid song name");
    const songs = songsCollection(gemeindeId);
    const id = songDocId(title);
    const byId = await getDoc(doc(songs, id));
    if (byId.exists()) throw new SongExistsError(String(byId.data().name ?? id));
    const byKey = await getDocs(query(songs, where("nameKey", "==", key), limit(1)));
    if (!byKey.empty) throw new SongExistsError(String(byKey.docs[0].data().name ?? byKey.docs[0].id));
    await setDoc(doc(songs, id), {
        name: title,
        nameKey: key,
        ...details,
        archived: false,
        performanceDates: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    });
    return id;
}

/** Changes everything about a song except its title (see renameChoirSong). */
export async function updateChoirSong(
    gemeindeId: string,
    songId: string,
    fields: Partial<ChoirSongDetails & { archived: boolean; unverified: boolean }>
): Promise<void> {
    await updateDoc(doc(songsCollection(gemeindeId), songId), { ...fields, updatedAt: serverTimestamp() });
}

export type RenameResult = {
    songId: string;
    name: string;
    merged: boolean;
    movedPerformances: number;
    droppedPerformances: number;
    updatedServices: number;
};

/**
 * Renames a song, or with `merge` moves it into the song that already has that
 * title. Runs in a Cloud Function: performances and the services listing the
 * song are rewritten too. Without `merge` a taken title is refused
 * (error code "functions/already-exists").
 */
export async function renameChoirSong(gemeindeId: string, songId: string, name: string, merge: boolean): Promise<RenameResult> {
    const rename = httpsCallable<{ gemeindeId: string; songId: string; name: string; merge: boolean }, RenameResult>(
        functions,
        "renameChoirSong",
        { timeout: 300000 }
    );
    return (await rename({ gemeindeId, songId, name, merge })).data;
}

export type PerformanceInRange = { date: string; songId: string; occasion: string; source: string };

/**
 * The performances of a period (both days included), read year by year through
 * the collection-group index. Only complete after the choir backfill has run.
 */
export async function loadPerformancesInRange(gemeindeId: string, from: string, to: string): Promise<PerformanceInRange[]> {
    const years: number[] = [];
    for (let year = Number(from.slice(0, 4)); year <= Number(to.slice(0, 4)); year++) years.push(year);
    const snapshots = await Promise.all(
        years.map((year) =>
            getDocs(
                query(
                    collectionGroup(db, Globals.CHOIRSONG_PERFORMANCES_SUBCOLLECTION),
                    where("gemeindeId", "==", gemeindeId),
                    where("year", "==", year)
                )
            )
        )
    );
    return snapshots
        .flatMap((snapshot) => snapshot.docs)
        .map((perfDoc) => ({
            date: String(perfDoc.data().date ?? ""),
            songId: perfDoc.ref.parent.parent!.id,
            occasion: perfDoc.data().occasion ? String(perfDoc.data().occasion) : "",
            source: String(perfDoc.data().source ?? ""),
        }))
        .filter((performance) => performance.date >= from && performance.date <= to);
}
